import React from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import { IOrder } from '../../../types';
import {
  getOrderCompanyName,
  getOrderCustomerName,
} from '../../../helpers/orders.helpers';
import { CheckoutLabel } from './checkout-label';

const money = (value?: number) =>
  value == null ? '—' : `₦${Number(value).toLocaleString('en-NG')}`;
const date = (value?: string | null) =>
  value && moment(value).isValid()
    ? moment(value).format('DD MMM YYYY, h:mm A')
    : '—';
const titleCase = (value?: string) =>
  value
    ? value
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/^./, (letter) => letter.toUpperCase())
    : 'Unknown';
const focus =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';

function Status({ value }: { value?: string }) {
  const color =
    value === 'CANCELED'
      ? 'bg-red-50 text-red-700'
      : ['DELIVERED', 'READY_FOR_PICKUP', 'CONFIRMED'].includes(value ?? '')
      ? 'bg-success-subtle text-green-800'
      : value === 'PREPARING'
      ? 'bg-warning-50 text-yellow-800'
      : 'bg-medium text-text';
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full px-2 text-[11px] leading-5 ${color}`}
    >
      {titleCase(value)}
    </span>
  );
}

function Skeleton() {
  return (
    <span
      aria-hidden="true"
      className="inline-block h-3.5 w-32 max-w-full rounded bg-neutral-200 motion-safe:animate-pulse"
    />
  );
}

function Field({
  label,
  value,
  loading,
}: {
  label: string;
  value?: React.ReactNode;
  loading: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="mb-1.5 text-xs text-neutral-500">{label}</dt>
      <dd className="break-words text-sm leading-5">
        {loading ? <Skeleton /> : value || '—'}
      </dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-slate-medium bg-white p-6">
      <h2 className="semibold mb-6 text-sm">{title}</h2>
      {children}
    </section>
  );
}

export type CheckoutError = 'forbidden' | 'missing' | 'failed';
export function CheckoutDetailsView({
  slug,
  order,
  error,
  onRetry,
}: {
  slug?: string;
  order?: IOrder;
  error?: CheckoutError;
  onRetry: () => void;
}) {
  const loading = !order && !error;
  const portions = order?.checkoutOrders ?? (order ? [order] : []);
  const company = getOrderCompanyName(order);
  const address = order?.deliveryAddress as IOrder['deliveryAddress'] & {
    state?: string;
    postalCode?: string;
  };
  const services = portions.flatMap(
    (portion) => portion.OrderService ?? []
  ) as Array<{ service?: string; type?: string }>;
  const deliveryType = Array.from(
    new Set(
      services.map((service) => service.service || service.type).filter(Boolean)
    )
  ).join(', ');
  const paymentMethods = Array.from(
    new Set(portions.map((portion) => portion.methodOfPayment).filter(Boolean))
  )
    .map(titleCase)
    .join(', ');
  const paymentStatus =
    !portions.length || portions.some((portion) => portion.paidFor == null)
      ? '—'
      : portions.every((portion) => portion.paidFor)
      ? 'Paid'
      : portions.some((portion) => portion.paidFor)
      ? 'Partially paid'
      : 'Pending';
  const rider = order?.pilot?.user;
  const heading =
    error === 'forbidden'
      ? 'You don’t have access to this order'
      : error === 'missing'
      ? 'Order not found'
      : 'Couldn’t load this order';
  return (
    <main className="h-full overflow-y-auto p-4 text-text sm:p-6">
      <Link
        to="/dashboard/orders"
        className={`mb-3 inline-flex min-h-[44px] items-center gap-2 text-sm ${focus}`}
      >
        <span aria-hidden="true">‹</span>All orders
      </Link>
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="bold break-all text-xl">Order ID #{slug}</h1>
          {loading ? <Skeleton /> : order && <Status value={order.status} />}
        </div>
        {!error && (
          <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-neutral-500">
            {order && <CheckoutLabel type={order.checkoutType} />}
            <span>
              {loading ? <Skeleton /> : `Created: ${date(order?.createdAt)}`}
            </span>
          </div>
        )}
      </div>
      {error ? (
        <section
          role="alert"
          className="rounded-xl border border-slate-medium p-6 py-14 text-center"
        >
          <h2 className="semibold text-lg">{heading}</h2>
          <p className="mt-3 text-sm text-neutral-500">
            {error === 'forbidden'
              ? 'Contact your administrator if you need access.'
              : error === 'missing'
              ? 'Check the order reference or return to all orders.'
              : 'Try again to load the order details.'}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            {error === 'failed' && (
              <button
                onClick={onRetry}
                className={`min-h-[44px] rounded-lg bg-primary px-5 text-sm text-white ${focus}`}
              >
                Try again
              </button>
            )}
            <Link
              to="/dashboard/orders"
              className={`inline-flex min-h-[44px] items-center rounded-lg border border-slate-medium px-5 text-sm ${focus}`}
            >
              Back to orders
            </Link>
          </div>
        </section>
      ) : (
        <div className="space-y-6" aria-busy={loading}>
          {loading && (
            <p role="status" className="sr-only">
              Loading order details
            </p>
          )}
          <Section title={company ? 'Company details' : 'Customer details'}>
            <dl className="grid gap-6 md:grid-cols-3">
              <Field
                label={company ? 'Company name' : 'Customer name'}
                value={getOrderCustomerName(order)}
                loading={loading}
              />
              <Field
                label="Email address"
                value={order?.customer?.email || order?.user?.email}
                loading={loading}
              />
              <Field
                label="Phone number"
                value={order?.customer?.phoneNumber || order?.user?.phoneNumber}
                loading={loading}
              />
              <Field
                label="Address"
                value={address?.address}
                loading={loading}
              />
              <Field
                label="State"
                value={address?.area || address?.state}
                loading={loading}
              />
              <Field
                label="Postal code"
                value={address?.postalCode}
                loading={loading}
              />
            </dl>
          </Section>
          <Section title="Delivery details">
            <dl className="grid gap-6 md:grid-cols-3">
              <Field
                label="Delivery type"
                value={deliveryType || 'Delivery'}
                loading={loading}
              />
              <Field
                label="Rider assigned"
                value={
                  rider
                    ? `${rider.firstName ?? ''} ${rider.lastName ?? ''}`.trim()
                    : 'Not assigned'
                }
                loading={loading}
              />
              <Field
                label="Expected delivery"
                value={
                  order?.status === 'CANCELED'
                    ? '—'
                    : date(order?.scheduledTime)
                }
                loading={loading}
              />
            </dl>
          </Section>
          <section className="overflow-hidden rounded-xl border border-slate-medium bg-white">
            <div className="flex items-start justify-between gap-4 p-6">
              <div>
                <h2 className="semibold mb-1 text-sm">Orders by brand</h2>
                <p className="text-sm text-neutral-500">
                  Open an order to view items and manage its status.
                </p>
              </div>
              <span className="shrink-0 text-sm text-neutral-500">
                {loading ? (
                  <Skeleton />
                ) : (
                  `${portions.length} ${
                    portions.length === 1 ? 'order' : 'orders'
                  }`
                )}
              </span>
            </div>
            {loading ? (
              [0, 1].map((key) => (
                <div
                  key={key}
                  aria-hidden="true"
                  className="grid gap-6 border-t border-slate-medium px-6 py-5 sm:grid-cols-4"
                >
                  <div className="space-y-2">
                    <Skeleton />
                    <br />
                    <Skeleton />
                  </div>
                  <Skeleton />
                  <Skeleton />
                  <Skeleton />
                </div>
              ))
            ) : portions.length ? (
              portions.map((portion) => (
                <Link
                  key={portion.id}
                  to={`/dashboard/order/${portion.id}`}
                  className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 border-t border-slate-medium px-6 py-5 hover:bg-primary-mild lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto_125px] lg:gap-6 ${focus}`}
                >
                  <div className="min-w-0">
                    <h3 className="semibold break-words text-sm">
                      {portion.brands?.map((brand) => brand.name).join(', ') ||
                        'Brand unavailable'}
                    </h3>
                    <p className="mt-1 break-words text-xs leading-5 text-neutral-500">
                      {portion.kitchens
                        ?.map((kitchen) => kitchen.name)
                        .join(', ') || 'Outlet unavailable'}{' '}
                      · #{portion.slug || portion.id}
                    </p>
                  </div>
                  <div className="col-span-2 row-start-2 min-w-0 text-sm lg:col-span-1 lg:row-auto">
                    <p className="break-words">
                      {portion.orderItems
                        ?.slice(0, 2)
                        .map(
                          (item) =>
                            `${item.foodItem?.name || 'Item'} × ${
                              item.quantity
                            }`
                        )
                        .join(', ') || 'No items available'}
                      {portion.orderItems?.length > 2 &&
                        ` +${portion.orderItems.length - 2} more`}
                    </p>
                    <p className="mt-1 text-xs text-neutral-500">
                      {money(portion.totalPrice)}
                    </p>
                  </div>
                  <Status value={portion.status} />
                  <span className="text-sm text-primary">
                    View order{' '}
                    <span aria-hidden="true" className="ml-2">
                      ›
                    </span>
                  </span>
                </Link>
              ))
            ) : (
              <div className="border-t border-slate-medium px-6 py-12 text-center">
                <h3 className="semibold text-sm">No brand orders available</h3>
                <p className="mt-2 text-sm text-neutral-500">
                  There are no individual orders to display for this checkout.
                </p>
              </div>
            )}
          </section>
          <Section title="Payment details">
            <dl className="grid grid-cols-2 gap-6 md:grid-cols-4">
              <Field
                label="Order total"
                value={money(order?.totalPrice)}
                loading={loading}
              />
              <Field
                label="Delivery fee"
                value={money(order?.deliveryCharge)}
                loading={loading}
              />
              <Field
                label="Payment method"
                value={paymentMethods}
                loading={loading}
              />
              <Field
                label="Payment status"
                value={paymentStatus}
                loading={loading}
              />
            </dl>
          </Section>
        </div>
      )}
    </main>
  );
}
